import React from 'react';

/**
 * Selected product entity
 *
 * Wraps up the logic for selecting a product as well as calculating price etc.
 *
 * @author John Noel <john.noel@joipolloi.com>
 * @package Minifigs
 */

class SelectedProduct {
    constructor(
        product,
        category,
        attributes = [],
        addons = [],
        product_attributes,
        attributeTerms,
        productFormJsonValues
    ) {
        this.product = product;
        this.category = category;
        this.attributes = attributes;
        this.addons = addons;
        this.productMatchAttr = product_attributes;
        this.attributeTerms = attributeTerms;
        this.productFormJsonValues = productFormJsonValues;
    }

    getPrice() {
        let price = 0;
        if (this.category.id === 'heads' && this.productMatchAttr?.priceV2) {
            price = parseFloat(this.productMatchAttr?.priceV2.amount);
        } else if (this.attributes?.priceV2) {
            price = parseFloat(this.attributes?.priceV2.amount);
        } else {
            if (this.product.variants.edges) {
                price = parseFloat(this.product.variants?.edges[0]?.node.priceV2?.amount);
            } else {
                price = parseFloat(this.product.variants.nodes && this.product.variants?.nodes[0]?.priceV2?.amount);
            }
        }

        const attr = this.attributes && this.attributes.length !== 0 ? this.attributes : this.product;
        const searchValue = attr?.id;

        if (attr && attr.length !== 0 && this.product.variants) {
            for (let i = 0; i < this.product.variants.length; i += 1) {
                const v = this.product.variants[i];
                if (v.id === searchValue) {
                    price = parseFloat(v.compare_at_price ? v.compare_at_price : v.price);
                    break;
                }
            }
        }

        let propCost = this.productFormJsonValues?.properties?._boldVariantPrices;
        if (propCost && propCost !== null && propCost !== 'undefined') {
            propCost = propCost.split(',');
            let totalPropCost = 0;
            propCost.forEach((cost) => {
                const costFloat = parseFloat(cost);
                totalPropCost += costFloat;
            });
            price = price.toFixed(2);
            price = price.toString().replace('.', '');
            price = parseFloat(price);
            price += totalPropCost;
            price = price.toString();
            price = `${price.substring(0, price.length - 2)}.${price.substring(price.length - 2)}`;
            price = parseFloat(price);
        }
        return price;
    }

    /**
     * This will get the image assigned to the particular variation
     * Mainly used in the overlay type of variable products
     *
     * @return {*} - string or none
     */
    getOverLayImage() {
        let image = (this.product.images && this.product.images[0] && this.product.images[0].src) || null;
        const attr = this.attributes;
        const searchValue = attr?.id;

        if (!this.product.images) {
            console.error('No images found');
            return image;
        }

        for (let i = 0; i < this.product.images.length; i += 1) {
            const v = this.product.images[i];
            if (v) {
                if (v.variant_ids[0]) {
                    if (searchValue === v.variant_ids[0] && v.variant_ids[0] !== undefined) {
                        image = v.src;
                    }
                }
            }
        }
        // this displays the main image(s) on the left of the select dropdown
        return image;
    }

    /**
     * Calculate the cost of the selected products
     *
     * @param {object} selected
     * @return {float}
     */
    static calculateCost(products) {
        let cost = 0.0;

        for (let k in products) {
            if (!products[k]) {
                continue;
            }

            if (typeof products[k].getPrice !== 'function') {
                products[k] = new SelectedProduct(
                    products[k].product,
                    products[k].category,
                    products[k].attributes,
                    products[k].addons,
                    products[k].productMatchAttr,
                    products[k].attributeTerms,
                    products[k].productFormJsonValues
                );
            }

            cost += products[k].getPrice();
        }

        return cost;
    }

    toJSON() {
        return {
            product: this.product,
            category: this.category,
            attributes: this.attributes,
            addons: this.addons,
            productMatchAttr: this.productMatchAttr,
            attributeTerms: this.attributeTerms,
            productFormJsonValues: this.productFormJsonValues,
        };
    }
}

export default SelectedProduct;
